import React, { useState } from 'react';
import { 
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter 
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { Bug, X, Upload, Loader2, Send } from 'lucide-react';
import { cn } from '../lib/utils';

export default function ReportBugModal({ open, onClose }) {
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        type: '',
        title: '',
        description: '',
        steps_to_reproduce: '',
        severity: 'medium'
    });

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.type || !formData.title || !formData.description) {
            toast.error('Please fill in all required fields');
            return;
        }

        setLoading(true);
        try {
            const token = localStorage.getItem('supabase.auth.token') || '';
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bugs/report`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(formData)
            });

            if (!response.ok) {
                const err = await response.json();
                throw new Error(err.detail || 'Failed to submit bug report');
            }

            toast.success('Bug reported successfully! Thank you for your feedback.');
            setFormData({
                type: '',
                title: '',
                description: '',
                steps_to_reproduce: '',
                severity: 'medium'
            });
            onClose();
        } catch (err) {
            toast.error(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-[500px] p-0 overflow-hidden bg-white dark:bg-zinc-950 border-zinc-200 dark:border-zinc-800 rounded-3xl shadow-2xl">
                <div className="bg-zinc-50 dark:bg-zinc-900/50 p-6 border-b border-zinc-100 dark:border-zinc-800">
                    <DialogHeader>
                        <div className="flex items-center gap-3 mb-1">
                            <div className="w-10 h-10 rounded-xl bg-blue-600/10 flex items-center justify-center text-blue-600">
                                <Bug className="w-6 h-6" />
                            </div>
                            <DialogTitle className="text-2xl font-black text-zinc-800 dark:text-zinc-100 tracking-tighter uppercase">
                                Report Issue
                            </DialogTitle>
                        </div>
                        <DialogDescription className="text-zinc-500 dark:text-zinc-400 text-sm">
                            Help us improve by describing the problem you encountered.
                        </DialogDescription>
                    </DialogHeader>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-5">
                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label className="text-xs font-bold text-zinc-500 uppercase tracking-widest ml-1">Issue Type *</Label>
                            <Select 
                                value={formData.type} 
                                onValueChange={(val) => setFormData({ ...formData, type: val })}
                            >
                                <SelectTrigger className="bg-zinc-50 dark:bg-zinc-900 border-zinc-200 dark:border-zinc-800 rounded-xl h-11">
                                    <SelectValue placeholder="Select type" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="ui">UI/UX Issue</SelectItem>
                                    <SelectItem value="functional">Functional Bug</SelectItem>
                                    <SelectItem value="performance">Performance</SelectItem>
                                    <SelectItem value="data">Data Accuracy</SelectItem>
                                    <SelectItem value="other">Other</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label className="text-xs font-bold text-zinc-500 uppercase tracking-widest ml-1">Severity</Label>
                            <Select 
                                value={formData.severity} 
                                onValueChange={(val) => setFormData({ ...formData, severity: val })}
                            >
                                <SelectTrigger className="bg-zinc-50 dark:bg-zinc-900 border-zinc-200 dark:border-zinc-800 rounded-xl h-11">
                                    <SelectValue placeholder="Severity" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="low">Low</SelectItem>
                                    <SelectItem value="medium">Medium</SelectItem>
                                    <SelectItem value="high">High</SelectItem>
                                    <SelectItem value="critical">Critical</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    <div className="space-y-2">
                        <Label className="text-xs font-bold text-zinc-500 uppercase tracking-widest ml-1">Summary *</Label>
                        <Input
                            placeholder="Short, descriptive title"
                            className="bg-zinc-50 dark:bg-zinc-900 border-zinc-200 dark:border-zinc-800 rounded-xl h-11"
                            value={formData.title}
                            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                            required
                        />
                    </div>

                    <div className="space-y-2">
                        <Label className="text-xs font-bold text-zinc-500 uppercase tracking-widest ml-1">Detailed Description *</Label>
                        <Textarea
                            placeholder="Describe the issue in detail..."
                            className="bg-zinc-50 dark:bg-zinc-900 border-zinc-200 dark:border-zinc-800 rounded-xl min-h-[100px] resize-none"
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            required
                        />
                    </div>

                    <div className="space-y-2">
                        <Label className="text-xs font-bold text-zinc-500 uppercase tracking-widest ml-1">Steps to Reproduce</Label>
                        <Textarea
                            placeholder="1. Go to... 2. Click on... 3. Observed..."
                            className="bg-zinc-50 dark:bg-zinc-900 border-zinc-200 dark:border-zinc-800 rounded-xl min-h-[80px] resize-none"
                            value={formData.steps_to_reproduce}
                            onChange={(e) => setFormData({ ...formData, steps_to_reproduce: e.target.value })}
                        />
                    </div>

                    {/* Attachments Placeholder (as in image) */}
                    <div className="space-y-2">
                        <Label className="text-xs font-bold text-zinc-500 uppercase tracking-widest ml-1">Attachments (Screenshots / Recording)</Label>
                        <div className="border-2 border-dashed border-zinc-200 dark:border-zinc-800 rounded-2xl p-6 flex flex-col items-center justify-center bg-zinc-50/50 dark:bg-zinc-900/30 hover:bg-zinc-100 dark:hover:bg-zinc-900/50 transition-colors cursor-pointer group">
                            <Upload className="w-6 h-6 text-zinc-400 group-hover:text-blue-600 transition-colors mb-2" />
                            <span className="text-sm font-bold text-zinc-600 dark:text-zinc-400">Click to choose a file or drag here</span>
                            <span className="text-[10px] text-zinc-400 uppercase tracking-widest mt-1">Size limit: 10 MB</span>
                        </div>
                    </div>

                    <div className="pt-2">
                        <Button 
                            type="submit" 
                            disabled={loading}
                            className="w-full h-12 bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 hover:bg-zinc-800 dark:hover:bg-zinc-200 font-black rounded-xl shadow-lg transition-all active:scale-[0.98] flex items-center justify-center gap-2"
                        >
                            {loading ? (
                                <Loader2 className="w-5 h-5 animate-spin" />
                            ) : (
                                <>
                                    <span>SUBMIT REPORT</span>
                                    <Send className="w-4 h-4" />
                                </>
                            )}
                        </Button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
}
